
import { useState, useEffect } from "react";
import { CalendarIcon } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarHeader } from "./CalendarHeader";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/use-theme";

interface BirthdatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
  className?: string;
}

export const BirthdatePicker = ({
  value,
  onChange,
  required,
  disabled = false,
  minYear = 1920,
  maxYear = new Date().getFullYear(),
  className = "",
}: BirthdatePickerProps) => {
  // birthdate visible in calendar (type Date | undefined)
  const [birthdateDate, setBirthdateDate] = useState<Date | undefined>(
    value && isValid(new Date(value)) ? new Date(value) : undefined
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    birthdateDate || new Date("2000-01-01")
  );

  const { theme, isLoading } = useTheme();

  // Sincronizar string <-> Date
  useEffect(() => {
    if (value && isValid(new Date(value))) {
      setBirthdateDate(new Date(value));
      setCalendarMonth(new Date(value));
    } else {
      setBirthdateDate(undefined);
    }
  }, [value]);

  // Cierra el calendar cuando se selecciona fecha
  const handleCalendarSelect = (date?: Date) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
      setBirthdateDate(date);
      setCalendarMonth(date);
      setCalendarOpen(false);
    }
  };

  // Permitir entrada manual en input, pero solo fechas válidas
  const handleBirthdateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onChange(value);
    if (value && isValid(new Date(value))) {
      const newDate = new Date(value);
      setBirthdateDate(newDate);
      setCalendarMonth(newDate);
    } else {
      setBirthdateDate(undefined);
    }
  };

  // Abre el calendario al hacer click en el input
  const handleDateFieldClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCalendarOpen(true);
  };

  // Manejar cambio de mes/año en el calendario
  const handleCalendarMonthChange = (newMonth: Date) => {
    setCalendarMonth(newMonth);
  };

  return (
    <div className={cn("relative flex flex-col space-y-1", className)}>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <div className="relative flex items-center text-white ">
            <Input
              id="birthdate"
              type="text"
              inputMode="none"
              autoComplete="off"
              pattern="\d{4}-\d{2}-\d{2}"
              placeholder="YYYY-MM-DD"
              value={value}
              onChange={handleBirthdateInput}
              required={required}
              disabled={disabled}
              className={cn(
                "pr-10 cursor-pointer",
                isLoading
                  ? "text-gray-400"
                  : [
                      "dark:text-white ocean:text-white forest:text-white sunset:text-white forest:bg-[#122112] ocean:bg-[#081F2B]",
                      !value && "text-muted-foreground",
                      theme === "light" && !["ocean", "sunset", "forest"].includes(theme) && "text-black"
                    ]
              )}
              onClick={handleDateFieldClick}
              readOnly
            />
            <CalendarIcon
              className="absolute right-3 text-muted-foreground h-5 w-5 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setCalendarOpen((v) => !v);
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent align="center" className="p-0 z-50 w-auto bg-black/50" sideOffset={8}>
          <Calendar
            mode="single"
            selected={birthdateDate}
            onSelect={handleCalendarSelect}
            month={calendarMonth}
            onMonthChange={handleCalendarMonthChange}
            fromYear={minYear}
            toYear={maxYear}
            components={{
              Caption: (props) => (
                <CalendarHeader
                  {...props}
                  fromYear={minYear}
                  toYear={maxYear}
                  onMonthChange={handleCalendarMonthChange}
                />
              ),
            }}
            disabled={(date) =>
              date > new Date() || date < new Date(`${minYear}-01-01`)
            }
            className="rounded-lg border p-2 bg-black/70 text-white pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
